import React, { useId, useState } from 'react';
import { Mail } from 'lucide-react';
import { subscribeToNewsletterApi } from '../../apis/newsletter';
import { track } from '../../lib/analytics';
import { useSnackbar } from '../../context/SnackbarContext';
import { Button, Input, Section, SectionHeading, accent, cn } from '../ui';

const NewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  // The snackbar disappears; the inline message stays next to the field that
  // caused it, which is what a screen reader and a returning eye both need.
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { showSnackbar } = useSnackbar();
  const fieldId = `${useId()}-newsletter-email`;

  const handleSubscribe = async (event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    setSuccessMessage(null);

    if (!email.trim()) {
      setError('Please enter your email address.');
      showSnackbar('Please enter your email address.', 'error');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      showSnackbar('Please enter a valid email address.', 'error');
      return;
    }

    setError(null);
    setIsSubscribing(true);
    try {
      await subscribeToNewsletterApi(email);
      track('newsletter_signup', { location: 'landing' });
      setSuccessMessage('Successfully subscribed to the newsletter!');
      showSnackbar('Successfully subscribed to the newsletter!', 'success');
      setEmail(''); // Clear input on success
    } catch (error) {
      console.error("Error subscribing to newsletter:", error);
      setError('Subscription failed. Please try again.');
      showSnackbar('Subscription failed. Please try again.', 'error');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    // The band itself stays light — the closing CTA above it is the page's one
    // inverted moment — so the newsletter earns its colour from a tinted panel
    // instead, with the white field sitting clearly on top of it.
    <Section>
      <div className={cn('mx-auto max-w-2xl overflow-hidden rounded-lg border border-border', accent('cyan').soft)}>
        <span aria-hidden="true" className={cn('block h-1', accent('cyan').rule)} />
        <div className="px-6 py-10 text-center sm:px-10">
          <SectionHeading
            align="center"
            className="mb-8"
            eyebrow="Newsletter"
            eyebrowAccent={accent('cyan').rule}
            title="Stay Updated"
            description="Subscribe to our newsletter for the latest resources, events, and academic insights."
          />

          <form onSubmit={handleSubscribe} className="text-left" noValidate>
            <label htmlFor={fieldId} className="sr-only">
              Email address
            </label>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
              <div className="min-w-0 flex-1">
                <Input
                  id={fieldId}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  placeholder="Enter your email"
                  leadingIcon={<Mail size={16} aria-hidden="true" />}
                  error={error ?? undefined}
                />
              </div>
              <Button type="submit" loading={isSubscribing} className="shrink-0">
                {isSubscribing ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </div>

            {successMessage && (
              <p role="status" className="mt-3 text-caption text-success">
                {successMessage}
              </p>
            )}
          </form>

          <p className="mt-6 text-caption text-text-secondary">
            We respect your privacy. Unsubscribe at any time.
          </p>
        </div>
      </div>
    </Section>
  );
};

export default NewsletterSection;
